// Toolbar.js
import React, { useState, useEffect } from 'react'
import { Icon, Switch, message, Popover } from 'antd'
import { builder } from '../../../constants'
import nanoid from 'nanoid'
import './Toolbar.scss'
import className from 'classnames'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import customImages from './Images/index'
import BackgroundImageSettings from './components/BackgroundImageSettings'
import BackgroundColorSettings from './components/BackgroundColorSettings'
import { useTour } from '../../Tour'

const { toolbar, types } = builder

const Toolbar = ({
  handleChangeBackground,
  handleData,
  handleParams,
  pageStructure = [],
  mode,
  isLandScape,
  enableGrid,
  currentPage,
  pages = [],
  pages_landscape = [],
  handleLessonInput,
  onAutoBackgroundChange,
  lessonBckgrndImg,
  lessonBckgrndImgId,
  handleShowAnnotationImageModal,
  handleShowAnnotationTextModal,
  handleShowAnnotationVideoModal,
  handleShowAnnotationAudioModal,
  handleShowAnnotationModal,
  handleShowAudioModal,
  handleShowImageModal,
  handleShowVideoModal,
  handleShowTextModal,
  handleShowQuizModal,
  handleShowGrid,
  handleCoursePreview
}) => {
  const { t } = useTranslation()
  const [showOptions, setShowOptions] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [showColorModal, setShowColorModal] = useState(false)
  const [selectedColor, setSelectedColor] = useState(null)
  const [tempColor, setTempColor] = useState('#ffffff')
  const [isApplyingAutoBackground, setIsApplyingAutoBackground] = useState(
    false
  )
  const { openTourModal, pauseTour, resumeTour, isTourActive, startTour } = useTour()
  const course = useSelector(state => state.courses?.courseById)
  const isDev = process.env.NODE_ENV === 'development'
  const isAiGeneratedCourse = isDev
    ? true
    : course?.item?.is_ai_generated

  useEffect(() => {
    const backgroundColor = pageStructure.find(
      node =>
        node.type === types.BACKGROUND_COLOR && node.params.content !== null
    )

    setSelectedColor(backgroundColor ? backgroundColor.params.content : null)
    setTempColor(backgroundColor ? backgroundColor.params.content : null)
  }, [pageStructure])

  const handleColorConfirm = () => {
    if (handleChangeBackground) {
      handleChangeBackground('color', tempColor)
      setSelectedColor(tempColor)
    }
    setShowColorModal(false)
  }

  const removeBackgroundElement = type => {
    if (handleData) {
      const data = pageStructure.filter(node => node.type !== type)
      handleData(data)
    }
    setShowImageModal(false)
    setShowColorModal(false)
  }

  const OptionsContent = () => (
    <div className="background-options" data-tour="background-indicator">
      <div
        className="background-option"
        data-tour="bg-option-image"
        onClick={() => {
          setShowOptions(false)
          const hasBackgroundImage = pageStructure.find(
            node => node.type === types.BACKGROUND_IMAGE
          )
          if (hasBackgroundImage) {
            setShowImageModal(true)
            if (isTourActive) {
              pauseTour()
              setTimeout(() => {
                startTour('background-image-settings')
              }, 1000)
            }
          } else {
            handleChangeBackground('image')
            if (isTourActive) {
              pauseTour()
            }
          }
        }}
      >
        <Icon type="picture" />
        <span>{t('v4:bg_image')}</span>
      </div>
      <div
        className="background-option"
        data-tour="bg-option-color"
        onClick={() => {
          setShowOptions(false)
          if (isTourActive) {
            pauseTour()
            setTimeout(() => {
              startTour('background-color-settings')
            }, 1000)
          }
          setShowColorModal(true)
        }}
      >
        <Icon type="bg-colors" />
        <span>{t('v4:bg_color')}</span>
      </div>
    </div>
  )

  const backgroundElement = pageStructure.find(
    node =>
      (node.type === types.BACKGROUND_IMAGE ||
        node.type === types.BACKGROUND_COLOR) &&
      node.params &&
      node.params.content !== null
  )

  const BackgroundIndicator = () => (
    <div
      className="toolbar__item"
      onClick={() => {
        if (backgroundElement) {
          if (backgroundElement.type === types.BACKGROUND_IMAGE) {
            setShowImageModal(true)
            if (isTourActive) {
              pauseTour()
              startTour('background-image-settings')
            }
          } else {
            setShowColorModal(true)
            if (isTourActive) {
              pauseTour()
              startTour('background-color-settings')
            }
          }
        } else {
          setShowOptions(visible => !visible)
        }
      }}
    >
      {backgroundElement ? (
        <div className="toolbar__item-icon background-preview">
          {backgroundElement.type === types.BACKGROUND_IMAGE ? (
            <div
              className="image-preview"
              style={{
                backgroundImage: `url(${backgroundElement.params.content})`,
                borderRadius: '50%',
                backgroundSize: 'cover',
                opacity: backgroundElement.params.opacity || 1
              }}
            />
          ) : (
            <div
              className="color-preview"
              style={{
                backgroundColor: selectedColor,
                borderRadius: '50%'
              }}
            />
          )}
        </div>
      ) : (
        <div className="toolbar__item-icon">
          {getToolbarImage(types.BACKGROUND_IMAGE)}
        </div>
      )}
      <div className="toolbar__item-text">{t('v4:background')}</div>
    </div>
  )

  const addElement = type => {
    if (handleData) {
      const width = type === types.TEXT ? 450 : 400

      const data = {
        uid: nanoid(8),
        type: type,
        params: {
          content: '',
          width: width,
          height: 200,
          x: 0,
          y: 0
        }
      }

      handleData(data, true)
    }
  }

  const handleToolClick = ({ type }) => {
    const pageHasScenario = pageStructure.some(
      node => node.type === types.SCENARIO
    )

    if (pageHasScenario) {
      message.error(t('v4:elemeent_in_a_sceenario_page'), 10)
      return
    }

    if (mode === types.ANNOTATION) {
      if (type === types.TEXT) {
        handleShowAnnotationTextModal()
      } else if (type === types.IMAGE) {
        handleShowAnnotationImageModal()
      } else if (type === types.VIDEO) {
        handleShowAnnotationVideoModal()
      } else if (type === types.AUDIO) {
        handleShowAnnotationAudioModal()
      }
    } else {
      if (type === types.AUDIO) {
        handleShowAudioModal()
      } else if (type === types.IMAGE) {
        handleShowImageModal(false)
      } else if (type === types.AR_IMAGE) {
        handleShowImageModal(true)
      } else if (type === types.VIDEO) {
        handleShowVideoModal()
      } else if (type === types.TEXT) {
        handleShowTextModal()
      } else if (type === types.ANNOTATION) {
        handleShowAnnotationModal()
      } else if (type === types.QUIZ) {
        handleShowQuizModal()
      } else if (type === types.GRID) {
        handleShowGrid()
      } else {
        addElement(type)
      }
    }
  }

  const onChangeAutoBgChange = () => {
    // Set flag to show the switch is turned on
    setIsApplyingAutoBackground(true)

    // Use setTimeout to add a small delay before applying the background change
    // This allows the switch to visually show the "on" state before disappearing
    setTimeout(() => {
      const pagesTemp = [...pages]
      const pagesLandscapeTemp = [...pages_landscape]
      const pageStructureTemp = [...pageStructure]

      // When enabling auto background, remove any existing background elements
      const filteredPageStructure = pageStructureTemp.filter(
        item =>
          item.type !== types.BACKGROUND_IMAGE &&
          item.type !== types.BACKGROUND_COLOR
      )

      // Create new background element with lesson background image
      if (lessonBckgrndImg !== null) {
        const newBackgroundData = {
          uid: nanoid(8),
          type: types.BACKGROUND_IMAGE,
          params: {
            content: lessonBckgrndImg,
            content_id: lessonBckgrndImgId,
            opacity: 1
          }
        }

        filteredPageStructure.push(newBackgroundData)

        // Update pages with the new structure
        pagesTemp[currentPage - 1].data = filteredPageStructure
        pagesLandscapeTemp[currentPage - 1].data = filteredPageStructure

        handleLessonInput('pages', pagesTemp)
        handleLessonInput('pages_landscape', pagesLandscapeTemp)

        // Call the callback to notify about background change
        if (onAutoBackgroundChange) {
          onAutoBackgroundChange()
        }
      }

      // Reset the flag
      setIsApplyingAutoBackground(false)
    }, 500) // 500ms delay for smooth transition
  }

  const getToolbarImage = type => {
    switch (type) {
      case types.ANNOTATION:
        return <img src={customImages.Annotation} alt="Annotation" />
      case types.AR_IMAGE:
        return <img src={customImages.ArImage} alt="AR" />
      case types.IMAGE:
        return <img src={customImages.Image} alt="Image" />
      case types.AUDIO:
        return <img src={customImages.Audio} alt="Audio" />
      case types.BACKGROUND_IMAGE:
        return <img src={customImages.Background} alt="Background" />
      case types.GRID:
        return <img src={customImages.Grid} alt="Grid" />
      case types.QUIZ:
        return <img src={customImages.Quiz} alt="Quiz" />
      case types.TEXT:
        return <img src={customImages.Text} alt="Text" />
      case types.VIDEO:
        return <img src={customImages.Video} alt="Video" />
      default:
        return null
    }
  }

  const items = toolbar.filter(item => item.mode.includes(mode))
  const classNames = className('toolbar__list', {
    toolbar__list__annotation: mode === types.ANNOTATION
  })

  // Check if there's a background element to determine if auto background should be disabled
  const hasBackgroundElement = !!pageStructure.find(
    node =>
      (node.type === types.BACKGROUND_IMAGE ||
        node.type === types.BACKGROUND_COLOR) &&
      node.params.content !== null
  )

  const hasAutoBackground = !!lessonBckgrndImgId

  return (
    <div className="toolbar">
      {mode !== types.ANNOTATION && (
        <div className="toolbar__preview" onClick={handleCoursePreview} data-tour="preview-course">
          <Icon type="play-circle" /> <span>{t('v4:preview_course')}</span>
        </div>
      )}
      <div className="toolbar__inner">
        <ul className={classNames}>
          {items.map((item, index) => {
            if (item.type === types.BACKGROUND_IMAGE) {
              return (
                <li key={index} data-tour="background-indicator">
                  <Popover
                    content={<OptionsContent />}
                    visible={showOptions && !backgroundElement}
                    onVisibleChange={visible => setShowOptions(visible)}
                    trigger="click"
                    placement="bottom"
                    overlayStyle={{ zIndex: 10000 }}
                  >
                    <div >
                      <BackgroundIndicator />
                    </div>
                  </Popover>
                </li>
              )
            }
            return (
              <li key={index}>
                {!isLandScape && (
                  <div
                    className={`toolbar__item ${enableGrid && item.type === 'GRID' ? 'active_grid' : ''
                      }`}
                    onClick={() => handleToolClick(item)}
                    data-tour={item.type}
                  >
                    <div className="toolbar__item-icon">
                      {getToolbarImage(item.type) || <Icon type={item.icon} />}
                    </div>
                    <div className="toolbar__item-text">{t(item.name)}</div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </div>
      {(!hasBackgroundElement || isApplyingAutoBackground) &&
        hasAutoBackground && (
          <div className="toolbar__autobg">
            <p>{t('v2:auto_background')}</p>
            <Switch
              checked={isApplyingAutoBackground}
              onChange={onChangeAutoBgChange}
              disabled={hasBackgroundElement && !isApplyingAutoBackground}
              loading={isApplyingAutoBackground}
            />
          </div>
        )}

      <BackgroundImageSettings
        visible={showImageModal}
        onCancel={() => {
          resumeTour()
          setShowImageModal(false)
        }}
        onRemove={() =>
          removeBackgroundElement(backgroundElement && backgroundElement.type)
        }
        onChangeImage={() => {
          if (isTourActive) {
            pauseTour()
          }
          handleChangeBackground('image')
          setShowImageModal(false)
          resumeTour()
        }}
        backgroundElement={backgroundElement}
        handleParams={handleParams}
        pageStructure={pageStructure}
      />
      <BackgroundColorSettings
        visible={showColorModal}
        onCancel={() => {
          resumeTour()
          setShowColorModal(false)
        }}
        onRemove={() => removeBackgroundElement(types.BACKGROUND_COLOR)}
        selectedColor={selectedColor}
        tempColor={tempColor}
        setTempColor={setTempColor}
        onConfirm={() => {
          handleColorConfirm()
          resumeTour()
        }}
      />

      <div
        className="toolbar__preview"
        onClick={() => openTourModal({ isAiGeneratedCourse })}
      >
        <Icon type="compass" /> <span>{t('tour:take_a_tour')}</span>
      </div>
    </div>
  )
}

export default Toolbar
